import './StandardNumberInput.css';
import React from 'react';

function isValidNumberInput(value) {
    return /^-?\d*\.?\d*$/.test(value);
}

function getNumberValidationError({ title, value, allowNegative, min, max, nullAllowed }) {
    if (value === '') {
        return nullAllowed ? '' : `${title} cannot be blank`;
    }

    if (value === '-' || value === '.' || value === '-.') {
        return `${title} must be a valid number`;
    }

    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
        return `${title} must be a valid number`;
    }

    if (!allowNegative && numberValue < 0) {
        return `${title} cannot be negative`;
    }

    if (min !== null && min !== undefined && numberValue < Number(min)) {
        return `${title} must be ${min} or more`;
    }

    if (max !== null && max !== undefined && numberValue > Number(max)) {
        return `${title} must be ${max} or less`;
    }

    return '';
}

export function StandardNumberInput({
    title,
    data,
    setData,
    allowNegative = false,
    min = null,
    max = null,
    step = 1,
    variant = 'standard',
    nullAllowed = true,
    onError = false,
    onErrorSet = () => {},
    errorText = '',
    errorTextSet = () => {},
    onChangeFunction = null,
    layout = 'vertical',
}) {
    function updateValue(value) {

        if (!isValidNumberInput(value)) {
            return;
        }

        const validationError = getNumberValidationError({
            title,
            value,
            allowNegative,
            min,
            max,
            nullAllowed,
        });

        if (validationError) {
            onErrorSet(true);
            errorTextSet(validationError);
        } else {
            onErrorSet(false);
            errorTextSet('');
        }

        setData(value);

        if (onChangeFunction) {
            onChangeFunction(value);
        }
    }

    function handleValueChange(event) {
        updateValue(event.target.value);
    }

    function stepValue(direction) {
        const currentNumber = Number(data || min || 0);
        const nextValue = currentNumber + (direction * Number(step || 1));
        let constrainedValue = nextValue;
        if (min !== null && min !== undefined) {
            constrainedValue = Math.max(constrainedValue, Number(min));
        }
        if (max !== null && max !== undefined) {
            constrainedValue = Math.min(constrainedValue, Number(max));
        }
        updateValue(String(constrainedValue));
    }

    const isStepper = variant === 'stepper';
    const inputType = isStepper ? 'number' : 'text';
    const wrapperClassName = isStepper ? 'sniInputStepperWrap' : '';
    const inputClassName = [
        onError ? 'sniNumberInput sniNumberInputError' : 'sniNumberInput',
        isStepper ? 'sniNumberInputStepper' : '',
    ].filter(Boolean).join(' ');
    const formRowClassName = [
        'sniFormRow',
        isStepper ? 'sniFormRowStepper' : '',
        layout === 'inline' ? 'sniFormRowInline' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={formRowClassName}>
            <div className='sniTitleDiv'>
                <label className='sniTitleLabel'>{title}</label>
            </div>
            <div className='sniInputDiv'>
                <div className={wrapperClassName}>
                    <input
                        className={inputClassName}
                        type={inputType}
                        inputMode='decimal'
                        value={data}
                        min={min ?? undefined}
                        max={max ?? undefined}
                        step={step}
                        onChange={handleValueChange}
                    />
                    {isStepper && (
                        <div className='sniStepperButtons'>
                            <button
                                type='button'
                                className='sniStepperButton'
                                onClick={() => stepValue(1)}
                                aria-label={`Increase ${title}`}
                            >
                                ▲
                            </button>
                            <button
                                type='button'
                                className='sniStepperButton'
                                onClick={() => stepValue(-1)}
                                aria-label={`Decrease ${title}`}
                            >
                                ▼
                            </button>
                        </div>
                    )}
                </div>
                <label className='sniErrorLabel'>{errorText}</label>
            </div>
        </div>
    );
}
